import { lazy } from "react";
import { Navigate } from "react-router";
import SettingsLayout from "../../_layouts/SettingsLayout";
import { FeatureFlagRoute } from "../../_features/featureFlags/routing/FeatureFlagRoute";
import PermissionRoute from "./PermissionRoute";
import { ROUTE_PERMISSIONS } from "./routePermissions";

const ProfilePage = lazy(() => import("../../_features/accountSettings/pages/ProfilePage"));
const SystemFeatures = lazy(() => import("../../_features/systemFeatures/pages/SystemFeaturesPage"));
const UsageSources = lazy(() => import("../../_features/usageSources/pages/UsageSourcesPage"));
const UsageCompilation = lazy(() => import("../../_features/usageCompilation/pages/UsageCompilationPage"));
const UsageCollection = lazy(() => import("../../_features/usageCollection/pages/UsageCollectionPage"));
const DocumentManagement = lazy(() => import("../../_features/documentManagement/pages/TemplateListPage"));
const DocumentTemplateEditor = lazy(() => import("../../_features/documentManagement/pages/TemplateEditorPage"));
const User = lazy(() => import("../../_features/userManagement/pages/UserManagementPage"));
const Roles = lazy(() => import("../../_features/roles/pages/RolesPage"));
const Permissions = lazy(() => import("../../_features/permissions/pages/PermissionsPage"));
const BillingSettings = lazy(() => import("../../_features/billingLogic/pages/BillingLogicPage"));
const BillingSettingsEditor = lazy(() => import("../../_features/billingLogic/pages/BillingLogicEditorPage"));
const Webhooks = lazy(() => import("../../_features/webhooks/pages/WebhooksPage"));
const OrgSettings = lazy(() => import("../../_features/accountSettings/pages/OrganisationDetailsPage"));
const SelfServeCheckoutConfiguration = lazy(() => import("../../_features/selfServeCheckoutConfig/pages/SelfServeCheckoutConfigPage"));
const PlatformSettings = lazy(() => import("../../_features/platformSettings/pages/PlatformSettingsPage"));
const ImportsPage = lazy(() => import("../../_features/imports/pages/ImportsPage"));
const ImportsInsiderViewPage = lazy(() => import("../../_features/imports/pages/ImportManageFieldsPage"));
const MigrationsPage = lazy(() => import("../../_features/migrations/pages/MigrationsPage"));
const MigrationDetailPage = lazy(() => import("../../_features/migrations/pages/MigrationDetailPage"));
const BillingUnitsPageFeature = lazy(() => import("../../_features/billingUnits/pages/BillingUnitsPage"));
const ExportPageFeature = lazy(() => import("../../_features/exports/pages/ExportsPage"));
const ObjectCustomization = lazy(() => import("../../_features/objectCustomization/pages/ObjectCustomizationPage"));
const RoleCreate = lazy(() => import("../../_features/roles/pages/RoleCreatePage"));
const RoleEdit = lazy(() => import("../../_features/roles/pages/RoleEditPage"));
const Integrations = lazy(() => import("../../_features/integrations/pages/IntegrationsPage"));
const TimeMachine = lazy(() => import("../../_features/time-machine/pages/TimeMachinePage"));

export const settingsRoutes = [
    {
        element: <SettingsLayout />,
        children: [
            {
                path: "/profile", element:
                    <FeatureFlagRoute feature="yourProfile">
                        <ProfilePage />
                    </FeatureFlagRoute>
            },
            {
                path: "/selfservecheckout/configure", element:
                    <FeatureFlagRoute feature="configureSelfCheckout">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.selfServeCheckoutConfig.edit} moduleName="Self-checkout configuration">
                            <SelfServeCheckoutConfiguration />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings", element:
                    <Navigate to="/settings/configurebilling/systemFeatures" />
            },
            {
                path: "/settings/configurebilling/billingunits", element:
                    <FeatureFlagRoute feature="billingUnits">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.billingUnits.view} moduleName="Billing units">
                            <BillingUnitsPageFeature />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/configurebilling/systemFeatures", element:
                    <FeatureFlagRoute feature="systemFeatures">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.systemFeatures.view} moduleName="System features">
                            <SystemFeatures />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/configurebilling/usageSources", element:
                    <FeatureFlagRoute feature="usageSources">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.usageSources.view} moduleName="Usage sources">
                            <UsageSources />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/userManagement", element:
                    <FeatureFlagRoute feature="userManagement">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.userManagement.view} moduleName="User management">
                            <User />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/roles", element:
                    <FeatureFlagRoute feature="roles">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.roles.view} moduleName="Roles">
                            <Roles />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/roles/create", element:
                    <FeatureFlagRoute feature="roles">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.roles.create} moduleName="Roles">
                            <RoleCreate />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/roles/:id/edit", element:
                    <FeatureFlagRoute feature="roles">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.roles.edit} moduleName="Roles">
                            <RoleEdit />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/permissions", element:
                    <FeatureFlagRoute feature="permissions">
                        <Permissions />
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/configurebilling/usageCompilation", element:
                    <FeatureFlagRoute feature="usageCompilation">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.usageCompilation.view} moduleName="Usage compilation">
                            <UsageCompilation />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/configurebilling/usageCollection", element:
                    <FeatureFlagRoute feature="usageCollection">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.usageCollection.view} moduleName="Usage collection">
                            <UsageCollection />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/organisationDetails", element:
                    <FeatureFlagRoute feature="organisationDetails">
                        <OrgSettings />
                    </FeatureFlagRoute>
            },
            {
                path: "/platformSettings", element:
                    <FeatureFlagRoute feature="platformSettings">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.platformSettings.view} moduleName="Platform settings">
                            <PlatformSettings />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/documentManagement", element:
                    <FeatureFlagRoute feature="documentManagement">
                        <DocumentManagement />
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/documentManagement/:templateId", element:
                    <FeatureFlagRoute feature="documentManagement">
                        <DocumentTemplateEditor />
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/imports", element:
                    <FeatureFlagRoute feature="imports">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.imports.view} moduleName="Imports">
                            <ImportsPage />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/exports", element:
                    <FeatureFlagRoute feature="exports">
                        <ExportPageFeature />
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/imports/manageFields/:importId", element:
                    <FeatureFlagRoute feature="imports">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.imports.manageFields} moduleName="Imports">
                            <ImportsInsiderViewPage />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/migrations", element:
                    <FeatureFlagRoute feature="migrations">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.migrations.view} moduleName="Migrations">
                            <MigrationsPage />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/migrations/:migrationId", element:
                    <FeatureFlagRoute feature="migrations">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.migrations.view} moduleName="Migrations">
                            <MigrationDetailPage />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/configurebilling/billingLogic", element:
                    <FeatureFlagRoute feature="billingLogic">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.billingLogic.view} moduleName="Billing logic">
                            <BillingSettings />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/configurebilling/billingLogic/:id/edit", element:
                    <FeatureFlagRoute feature="billingLogic">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.billingLogic.edit} moduleName="Billing logic">
                            <BillingSettingsEditor />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/configurebilling/billingLogic/create", element:
                    <FeatureFlagRoute feature="billingLogic">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.billingLogic.create} moduleName="Billing logic">
                            <BillingSettingsEditor />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/configurebilling/billingLogic/:id/view", element:
                    <FeatureFlagRoute feature="billingLogic">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.billingLogic.view} moduleName="Billing logic">
                            <BillingSettingsEditor />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/settings/objectCustomization", element:
                    <FeatureFlagRoute feature="objectCustomization">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.objectCustomization.view} moduleName="Object customization">
                            <ObjectCustomization />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/configure/webhooks", element:
                    <FeatureFlagRoute feature="webhooks">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.webhooks.view} moduleName="Webhooks">
                            <Webhooks />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/configure/integrations", element:
                    <FeatureFlagRoute feature="integrations">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.integrations.view} moduleName="Integrations">
                            <Integrations />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
            {
                path: "/configure/time-machine", element:
                    <FeatureFlagRoute feature="timeMachine">
                        <PermissionRoute permission={ROUTE_PERMISSIONS.timeMachine.view} moduleName="Time Machine">
                            <TimeMachine />
                        </PermissionRoute>
                    </FeatureFlagRoute>
            },
        ]
    }
];
